import React, { memo, useCallback, useState } from 'react';

import {
  phoneConstant,
  zipCodeValidationRegExp,
} from '_constants/otherConstants';

import BaseModal, { BaseModalProps } from 'app/components/Modals/BaseModal';

import useTenantTranslation from 'utils/hooks/useTenantTranslation';
import { useMediaQuery } from 'react-responsive';

import './style.scss';
import { MdOutlineEdit } from 'react-icons/md';
import useTenantColors from 'utils/hooks/useTenantColors';
import Textarea from 'app/components/Textarea/Textarea';
import CheckMarkIcon from 'app/components/Icons/CheckMarkIcon';
import { useAppSelector } from 'store/configureStore';
import { tenantSettingsTenantStylingSelector } from 'store/selectors/tenant-settings';
import { CgArrowRightO } from 'react-icons/cg';
import { ZipCodesTextInputValidationError } from './types';
import FieldWrapper from '../ProgramAndResourcesModal/utils/FieldWrapper';

const modalClassNames: BaseModalProps['classNames'] = {
  modal: 'zip_codes_text_input_modal',
  modalFooter: 'zip_codes_text_input_modal_footer',
  closeIcon: 'zip_codes_text_input_modal_closeIcon',
  acceptButton: 'zip_codes_text_input_modal__acceptButton',
  declineButton: 'zip_codes_text_input_modal__declineButton',
  modalInnerContainer: 'zip_codes_text_input_modal_innerContainer',
};

const successModalClassNames: BaseModalProps['classNames'] = {
  modal: 'zip_codes_text_input_success_modal',
  modalFooter: 'zip_codes_text_input_success_modal_footer',
  modalInnerContainer: 'zip_codes_text_input_success_modal_innerContainer',
};

interface ZipCodesTextInputModalProps {
  setIsSuccessfullyUploaded: (value: boolean) => void;
  onModalClose: () => void;
  onChange: (value: string[]) => void;
}

const ZipCodesTextInputModal: React.FC<ZipCodesTextInputModalProps> = memo(
  ({ setIsSuccessfullyUploaded, onChange, onModalClose }) => {
    const { t } = useTenantTranslation();
    const { colorVariables } = useTenantColors();
    const isPhone = useMediaQuery({ query: `(max-width: ${phoneConstant}px)` });
    const [validationError, setValidationError] =
      useState<ZipCodesTextInputValidationError>(null);
    const [isSuccessful, setIsSuccessful] = useState<boolean>(false);
    const [zipCodes, setZipCodes] = useState<string>('');
    const tenantStyle = useAppSelector(tenantSettingsTenantStylingSelector);
    const handleInputChange = (e: string) => {
      setZipCodes(e);
    };

    const onSuccess = useCallback(() => {
      setIsSuccessfullyUploaded(true);
      setIsSuccessful(true);
    }, [setIsSuccessful, setIsSuccessfullyUploaded, onModalClose]);

    const handleSave = useCallback(() => {
      try {
        const zipCodesToUpload = zipCodes
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);

        // eslint-disable-next-line no-restricted-syntax
        for (const zipCode of zipCodesToUpload) {
          if (!zipCodeValidationRegExp.test(zipCode)) {
            setValidationError({
              title: t(
                'onboarding.advisor_zip_codes.errors.text_input_modal.invalid_zip_code.title',
              ),
              text: t(
                'onboarding.advisor_zip_codes.errors.text_input_modal.invalid_zip_code.text',
              ),
            });
            return;
          }
        }

        if (!zipCodesToUpload.length) {
          setValidationError({
            title: t(
              'onboarding.advisor_zip_codes.errors.text_input_modal.empty.title',
            ),
            text: t(
              'onboarding.advisor_zip_codes.errors.text_input_modal.empty.text',
            ),
          });
          return;
        }

        onChange(zipCodesToUpload);

        onSuccess();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);

        setValidationError({
          title: t('onboarding.advisor_zip_codes.errors.unknown_error.title'),
          text: t('onboarding.advisor_zip_codes.errors.unknown_error.text'),
        });
      }
    }, [zipCodes]);

    return (
      <>
        {isSuccessful ? (
          <BaseModal
            closeOnBackgroundClick
            onModalClose={onModalClose}
            withCloseButton
            closeButtonLook="filled"
            closeButtonText={t('common.continue')}
            classNames={successModalClassNames}
            closeButtonIcon={
              <CgArrowRightO
                size={18}
                color={tenantStyle?.colorVariables.icons.icon_primary1_button}
              />
            }
          >
            <CheckMarkIcon
              isThin
              color={tenantStyle?.colorVariables.text.text_accent_1}
            />
            <p>
              {t(
                'onboarding.advisor_zip_codes.text_input_modal.zip_codes_uploaded_successful',
              )}
            </p>
          </BaseModal>
        ) : (
          <BaseModal
            closeOnBackgroundClick
            withAcceptButton
            onModalClose={onModalClose}
            acceptButtonText={t('common.save')}
            acceptButtonSize={isPhone ? 'small' : 'large'}
            onAcceptButtonClick={handleSave}
            withDeclineButton
            declineButtonText={t('common.cancel')}
            declineButtonSize={isPhone ? 'small' : 'large'}
            declineButtonLook="filled-gray"
            onDeclineButtonClick={onModalClose}
            classNames={modalClassNames}
            isCloseIconVisible
          >
            <MdOutlineEdit
              size={24}
              color={colorVariables?.icons.icon_secondary_tetriary_button}
            />
            <p className="zip_codes_text_input_modal_content_title">
              {isPhone
                ? t('onboarding.advisor_zip_codes.text_input_modal.phone_title')
                : t('onboarding.advisor_zip_codes.text_input_modal.title')}
            </p>
            <p className="zip_codes_text_input_modal_content_sub_title">
              {t('onboarding.advisor_zip_codes.text_input_modal.sub_title')}
            </p>
            <FieldWrapper
              title={t(
                'onboarding.advisor_zip_codes.text_input_modal.field_title',
              )}
              subTitle={t(
                'onboarding.advisor_zip_codes.text_input_modal.field_sub_title',
              )}
              fieldClassName="zip_codes_text_input_modal_content_field"
            >
              <Textarea
                type="text"
                placeholder={t(
                  'onboarding.advisor_zip_codes.text_input_modal.placeholder',
                )}
                value={zipCodes || ''}
                onChange={handleInputChange}
                maxLength={500}
                errors={validationError?.text}
              />
            </FieldWrapper>
          </BaseModal>
        )}
      </>
    );
  },
);

export default ZipCodesTextInputModal;
